/**
 * Inspiration library screen.
 *
 * UX
 *   - A "paste anywhere" zone at the top: drag-drop a file, paste a
 *     clipboard image (Cmd+V), or paste a URL (http(s)://… into the
 *     URL input). Mirrors the Pinterest workflow — right-click image
 *     → Copy Image → Cmd+V here.
 *   - Below: a responsive grid of saved tiles. Each tile shows the
 *     image, an editable note, and a delete button.
 *
 * Storage is server-side; this screen is the only consumer of the
 * /api/inspiration endpoints today. Future work will let the operator
 * pick 1-3 tiles when generating a post to bias GPT Image toward
 * curated references.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Clipboard, Loader2, Trash2 } from "lucide-react";
import { useIsMobile } from "../lib/useIsMobile";
import {
  addInspiration,
  deleteInspiration,
  entrySource,
  inspirationImageUrl,
  listInspiration,
  patchInspirationNote,
  type InspirationEntry,
} from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";

/** Browser-side helper: convert a File / Blob to a base64 data URL.
 *  The server expects the dataUrl shape "data:image/png;base64,…". */
function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error("FileReader error"));
    r.readAsDataURL(file);
  });
}

export function Inspiration(): React.JSX.Element {
  const isMobile = useIsMobile();
  const [entries, setEntries] = useState<InspirationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  // Source filter: All / External (Pinterest etc.) / Generated
  // (mirrored from posts we shipped). Default to All so the operator
  // doesn't lose sight of the generated archive on first load.
  const [sourceFilter, setSourceFilter] = useState<"all" | "external" | "generated">("all");
  const fileRef = useRef<HTMLInputElement | null>(null);

  const fetchEntries = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await listInspiration());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchEntries(); }, [fetchEntries]);

  // ---------- ingestion ------------------------------------------------

  const handleAddFromBlob = useCallback(async (
    blob: Blob,
    sourceUrl?: string,
  ): Promise<void> => {
    if (!blob.type.startsWith("image/")) {
      setError(`not an image: ${blob.type || "(unknown type)"}`);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const dataUrl = await fileToDataUrl(blob);
      await addInspiration({ dataUrl, sourceUrl });
      await fetchEntries();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }, [fetchEntries]);

  const handleAddFromUrl = useCallback(async (url: string): Promise<void> => {
    if (!/^https?:\/\//i.test(url)) {
      setError("URL must start with http:// or https://");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await addInspiration({ url });
      setUrlInput("");
      await fetchEntries();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }, [fetchEntries]);

  // ---------- paste / drag-drop hooks ---------------------------------

  // Listen for Cmd+V anywhere on the page. We DO NOT bind to a specific
  // input — Pinterest's "Copy Image" puts the binary on the clipboard
  // and the user wants to paste anywhere on this screen. We bail out if
  // the user is editing a text input so we don't hijack regular pastes.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      const editingText =
        tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable === true;
      const items = e.clipboardData?.items;
      if (!items) return;

      // Try image first — works even when an input is focused (Cmd+V on
      // a Pinterest image puts BOTH a text URL and the image blob on
      // the clipboard; we prefer the binary).
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            void handleAddFromBlob(file);
            return;
          }
        }
      }

      // Fall back to URL paste — only when no input is focused so we
      // don't steal a normal text paste inside the note field.
      if (editingText) return;
      for (const item of items) {
        if (item.kind === "string" && item.type === "text/plain") {
          item.getAsString((s) => {
            const url = s.trim();
            if (/^https?:\/\/.+\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(url)) {
              void handleAddFromUrl(url);
            }
          });
          return;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handleAddFromBlob, handleAddFromUrl]);

  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleAddFromBlob(file);
  };

  // ---------- render ---------------------------------------------------

  return (
    <div>
      <PageHeader
        eyebrow="Library · Inspiration"
        title="Moodboard for atmospheric references."
        subtitle="Paste images you find on Pinterest (Cmd+V), drag a file, or drop a URL. These will later seed the GPT Image prompts so the look stays consistent with what inspires you."
      />

      {/* Paste / drop target */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          marginBottom: 20,
          padding: "28px 24px",
          borderRadius: 16,
          border: `2px dashed ${dragOver ? "rgba(255,213,0,.55)" : "rgba(255,255,255,.15)"}`,
          background: dragOver ? "rgba(255,213,0,.06)" : "rgba(255,255,255,.02)",
          textAlign: "center",
          cursor: "pointer",
          transition: "all .15s var(--ql-ease)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
          {uploading ? (
            <Loader2 size={22} className="ql-spin" color="var(--ql-yellow)" />
          ) : (
            <Clipboard size={22} color="var(--ql-yellow)" />
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ql-fg)", marginBottom: 4 }}>
          {uploading ? "Uploading…" : "Paste, drop, or click to add an inspiration image"}
        </div>
        <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", lineHeight: 1.6 }}>
          Cmd+V a copied image · drag-drop a file · or paste an image URL below
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleAddFromBlob(f);
            e.target.value = "";
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && urlInput.trim() && !uploading) {
              void handleAddFromUrl(urlInput.trim());
            }
          }}
          placeholder="https://i.pinimg.com/…/image.jpg"
          disabled={uploading}
          style={{
            flex: 1,
            padding: "10px 14px",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,.10)",
            background: "rgba(255,255,255,.04)",
            color: "var(--ql-fg)",
            fontSize: 12,
            fontFamily: "var(--ql-font-mono)",
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={() => urlInput.trim() && void handleAddFromUrl(urlInput.trim())}
          disabled={uploading || !urlInput.trim()}
          style={{
            padding: "10px 18px",
            borderRadius: 12,
            border: "1px solid rgba(255,213,0,.30)",
            background: "rgba(255,213,0,.10)",
            color: "var(--ql-yellow)",
            fontSize: 12,
            fontWeight: 700,
            cursor: uploading ? "wait" : "pointer",
            opacity: !urlInput.trim() ? 0.55 : 1,
            fontFamily: "inherit",
            width: isMobile ? "100%" : undefined,
          }}
        >
          Add URL
        </button>
      </div>

      {error && (
        <div style={{
          marginBottom: 18,
          padding: "10px 14px",
          borderRadius: 12,
          background: "rgba(248,113,113,.10)",
          border: "1px solid rgba(248,113,113,.30)",
          color: "#FCA5A5",
          fontSize: 12,
        }}>{error}</div>
      )}

      {/* Source filter pills. Counts reflect the unfiltered set so the
          operator can see how many tiles each bucket holds without
          flipping between filters. */}
      {(() => {
        const externalCount = entries.filter((e) => entrySource(e) === "external").length;
        const generatedCount = entries.filter((e) => entrySource(e) === "generated").length;
        const pills: Array<{ id: typeof sourceFilter; label: string; count: number }> = [
          { id: "all", label: "All", count: entries.length },
          { id: "external", label: "External", count: externalCount },
          { id: "generated", label: "Generated", count: generatedCount },
        ];
        return (
          <div style={{ display: "flex", gap: 6, marginBottom: 18, alignItems: "center" }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: ".14em",
              color: "var(--ql-fg-subtle)", textTransform: "uppercase", marginRight: 4,
            }}>Source</span>
            {pills.map(({ id, label, count }) => {
              const selected = sourceFilter === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSourceFilter(id)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${selected ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.08)"}`,
                    background: selected ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
                    color: selected ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {label}
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: selected ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                    opacity: 0.75,
                  }}>{count}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        const visible = entries.filter((e) =>
          sourceFilter === "all" ? true : entrySource(e) === sourceFilter,
        );
        if (loading) {
          return (
            <div style={{ color: "var(--ql-fg-muted)", padding: "40px 0", textAlign: "center" }}>
              Loading…
            </div>
          );
        }
        if (visible.length === 0) {
          return (
            <div style={{
              padding: "60px 24px",
              textAlign: "center",
              border: "1px dashed rgba(255,255,255,.08)",
              borderRadius: 16,
              color: "var(--ql-fg-muted)",
              fontSize: 13,
              lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ql-fg)", marginBottom: 10 }}>
                {sourceFilter === "generated"
                  ? "No generated tiles yet"
                  : sourceFilter === "external"
                    ? "No external references yet"
                    : "No inspirations yet"}
              </div>
              {sourceFilter === "generated"
                ? "Tiles are added here automatically when a beta-post is scheduled to Buffer."
                : "Paste your first reference image above to start a moodboard."}
            </div>
          );
        }
        return (
          // CSS column masonry — Pinterest-style varied-height feel.
          // Tiles get `break-inside: avoid` so they never split across
          // columns; flowing items into columns rather than a grid is
          // what makes the rhythm read like a moodboard instead of a
          // regular tile grid.
          <div style={{
            columnCount: "auto",
            columnWidth: 240,
            columnGap: 14,
          }}>
            {visible.map((e) => (
              <InspirationTile
                key={e.id}
                entry={e}
                onChange={() => void fetchEntries()}
                onError={(msg) => setError(msg)}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function InspirationTile({
  entry,
  onChange,
  onError,
}: {
  entry: InspirationEntry;
  onChange: () => void;
  onError: (msg: string) => void;
}): React.JSX.Element {
  const [note, setNote] = useState(entry.note ?? "");
  const [savingNote, setSavingNote] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const commitNote = async (): Promise<void> => {
    if ((entry.note ?? "") === note.trim()) return;
    setSavingNote(true);
    try {
      await patchInspirationNote(entry.id, note.trim() || null);
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingNote(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    setConfirmDeleteOpen(false);
    try {
      await deleteInspiration(entry.id);
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
    <div style={{
      display: "flex", flexDirection: "column",
      borderRadius: 14,
      border: "1px solid rgba(255,255,255,.06)",
      background: "rgba(16,18,27,.6)",
      overflow: "hidden",
      // Column-masonry siblings: never split a tile across columns,
      // and bottom margin replaces the lost row gap.
      breakInside: "avoid",
      marginBottom: 14,
    }}>
      <a
        href={entry.sourceUrl || inspirationImageUrl(entry.id)}
        target="_blank"
        rel="noreferrer"
        style={{ display: "block", lineHeight: 0, position: "relative" }}
      >
        <img
          src={inspirationImageUrl(entry.id)}
          alt={entry.note ?? "Inspiration"}
          style={{
            width: "100%", height: "auto",
            display: "block",
            background: "#0A0908",
          }}
          loading="lazy"
        />
        {entrySource(entry) === "generated" && (
          // Top-right badge marking this as a tile WE generated, not an
          // external reference. Important so the operator never confuses
          // their own moodboard with their own shipped output.
          <span style={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "3px 8px",
            borderRadius: 999,
            background: "rgba(255,213,0,.18)",
            border: "1px solid rgba(255,213,0,.45)",
            color: "var(--ql-yellow)",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: ".12em",
            textTransform: "uppercase",
            pointerEvents: "none",
            backdropFilter: "blur(4px)",
          }}>Generated</span>
        )}
      </a>
      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => void commitNote()}
          placeholder="Note (optional)"
          rows={2}
          style={{
            width: "100%",
            padding: "6px 8px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,.08)",
            background: "rgba(255,255,255,.03)",
            color: "var(--ql-fg)",
            fontSize: 11,
            fontFamily: "inherit",
            resize: "vertical",
            minHeight: 38,
            outline: "none",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{
            fontSize: 10, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)",
          }}>
            {new Date(entry.addedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            {savingNote && " · saving…"}
          </span>
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            aria-label="Delete inspiration"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid rgba(248,113,113,.20)",
              background: "rgba(248,113,113,.06)",
              color: "var(--ql-danger)",
              fontSize: 10, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <Trash2 size={11} strokeWidth={2} />
            Delete
          </button>
        </div>
      </div>
    </div>
    <ConfirmDialog
      open={confirmDeleteOpen}
      title="Delete this inspiration tile?"
      message="The image and its note will be removed. You can always paste it back."
      confirmLabel="Delete"
      danger
      onConfirm={() => void confirmDelete()}
      onCancel={() => setConfirmDeleteOpen(false)}
    />
    </>
  );
}
